// Parameter threshold configuration
// Updated to match exact US-EPA limits and technical specifications from images
// Each entry: { good: [min, max], warn: [min, max], range: [min, max] } 
// good = US-EPA limits, warn = warning zone, range = technical measurement range

const parameterRanges = {
  // 1. Ammonia (NH3) - US-EPA Limit: 0.5 to 32.5 mg/L, Range: 0-100 mg/L
  ammonia_mg_l_avg: {
    good: [0.5, 32.5],
    warn: [0, 50],
    range: [0, 100],
    unit: ' mg/L',
    rangeLabel: '0–100 mg/L',
    goodLabel: '0.5–32.5 mg/L'
  },
  // 2. Alkalinity - US-EPA Limit: 20 to 200 mg/L, Range: 10-250 mg/L
  alkalinity_mg_l_avg: {
    good: [20, 200],
    warn: [10, 250],
    range: [10, 250],
    unit: ' mg/L',
    rangeLabel: '10–250 mg/L',
    goodLabel: '20–200 mg/L'
  },
  // 3. BOD - US-EPA Limit: <10 mg/L, Range: 0-500 mg/L
  bod_mg_l_avg: {
    good: [0, 10],
    warn: [0, 20],
    range: [0, 500],
    unit: ' mg/L',
    rangeLabel: '0–500 mg/L',
    goodLabel: '< 10 mg/L'
  },
  // 4. COD - US-EPA Limit: <100 mg/L, Range: 0-1000 mg/L
  cod_mg_l_avg: {
    good: [0, 100],
    warn: [0, 200],
    range: [0, 1000],
    unit: ' mg/L',
    rangeLabel: '0–1000 mg/L',
    goodLabel: '< 100 mg/L'
  },
  // 5. Chloride - US-EPA Limit: 45-155 mg/L, Range: 1-500 mg/L
  chloride_mg_l_avg: {
    good: [45, 155],
    warn: [20, 300],
    range: [1, 500],
    unit: ' mg/L',
    rangeLabel: '1–500 mg/L',
    goodLabel: '45–155 mg/L'
  },
  // 6. Color - US-EPA Limit: 15 Hazen, Range: 0-500 Hazen
  color_hazen_avg: {
    good: [0, 15],
    warn: [0, 30],
    range: [0, 500],
    unit: ' Hazen',
    rangeLabel: '0–500 Hazen',
    goodLabel: '≤ 15 Hazen'
  },
  // 7. Dissolved Oxygen - US-EPA Limit: 6.5-8 mg/L, Range: 0-20 mg/L
  dissolved_oxygen_mg_l_avg: {
    good: [6.5, 8.0],
    warn: [4, 10],
    range: [0, 20],
    unit: ' mg/L',
    rangeLabel: '0–20 mg/L',
    goodLabel: '6.5–8 mg/L'
  },
  // 8. TOC - US-EPA Limit: <5 mg/L, Range: 0-500 mg/L
  toc_mg_l_avg: {
    good: [0, 5],
    warn: [0, 10],
    range: [0, 500],
    unit: ' mg/L',
    rangeLabel: '0–500 mg/L',
    goodLabel: '< 5 mg/L'
  },
  // 9. DOC - US-EPA Limit: <4 mg/L, Range: 0-500 mg/L
  doc_mg_l_avg: {
    good: [0, 4],
    warn: [0, 8],
    range: [0, 500],
    unit: ' mg/L',
    rangeLabel: '0–500 mg/L',
    goodLabel: '< 4 mg/L'
  },
  // 10. Hydro Carbon/BTX - US-EPA Limit: <10 mg/L, Range: 0-20 mg/L
  hydrocarbon_mg_l_avg: {
    good: [0, 10],
    warn: [0, 15],
    range: [0, 20],
    unit: ' mg/L',
    rangeLabel: '0–20 mg/L',
    goodLabel: '< 10 mg/L'
  },
  // 11. Nitrate (NO3) - US-EPA Limit: <10 mg/L, Range: 0-50 mg/L
  nitrate_mg_l_avg: {
    good: [0, 10],
    warn: [0, 20],
    range: [0, 50],
    unit: ' mg/L',
    rangeLabel: '0–50 mg/L',
    goodLabel: '< 10 mg/L'
  },
  // 12. pH - US-EPA Limit: 6.5-9.0, Range: 0-14 pH
  ph_avg: {
    good: [6.5, 9.0],
    warn: [6.0, 10.0],
    range: [0, 14],
    unit: '',
    rangeLabel: '0–14 pH',
    goodLabel: '6.5–9 pH'
  },
  // 13. Redox/ORP - US-EPA Limit: 300-500 mV, Range: 0-1000 mV
  redox_mv_avg: {
    good: [300, 500],
    warn: [200, 700],
    range: [0, 1000],
    unit: ' mV',
    rangeLabel: '0–1000 mV',
    goodLabel: '300–500 mV'
  },
  // 14. Temperature - US-EPA Limit: 15-25°C, Range: -5 to 50°C
  temperature_c_avg: {
    good: [15, 25],
    warn: [10, 30],
    range: [-5, 50],
    unit: '°C',
    rangeLabel: '-5–50 °C',
    goodLabel: '15–25 °C'
  },
  // 15. TSS - US-EPA Limit: <100 mg/L, Range: 0-1000 mg/L TSS
  tss_mg_l_avg: {
    good: [0, 100],
    warn: [0, 200],
    range: [0, 1000],
    unit: ' mg/L',
    rangeLabel: '0–1000 mg/L TSS',
    goodLabel: '< 100 mg/L'
  },
  // 16. Turbidity - Range: 0-1000 NTU (no established US-EPA limit for river water)
  turbidity_ntu_avg: {
    good: [0, 5],
    warn: [0, 20],
    range: [0, 1000],
    unit: ' NTU',
    rangeLabel: '0–1000 NTU'
  },
  // 17. Ortho Phosphates - US-EPA Limit: <100 µg/L (0.1 mg/L), Range: 0-20 mg/L
  ortho_phosphates_mg_l_avg: {
    good: [0, 0.1],
    warn: [0, 0.2],
    range: [0, 20],
    unit: ' mg/L',
    rangeLabel: '0–20 mg/L',
    goodLabel: '< 100 µg/L (0.1 mg/L)'
  },
  // 18a. Cadmium - US-EPA Limit: Non-Detectable, Range: 0-5 mg/L
  cadmium_mg_l_avg: {
    good: [0, 0.001],
    warn: [0, 0.005],
    range: [0, 5],
    unit: ' mg/L',
    rangeLabel: '0.0–5 mg/L',
    goodLabel: 'Non-detectable'
  },
  // 18b. Lead - US-EPA Limit: Non-Detectable, Range: 0-5 mg/L
  lead_mg_l_avg: {
    good: [0, 0.001],
    warn: [0, 0.005],
    range: [0, 5],
    unit: ' mg/L',
    rangeLabel: '0.0–5 mg/L',
    goodLabel: 'Non-detectable'
  },
  // 18c. Nickel - Range: 0–500 µg/L (native unit)
  nickel_ug_l_avg: {
    good: [0, 100],
    warn: [0, 500],
    range: [0, 500],
    unit: ' µg/L',
    rangeLabel: '0–500 µg/L',
    goodLabel: 'Non-detectable'
  },
  // 18d. Chromium - Range: 0–150 ppb (native unit)
  chromium_ppb_avg: {
    good: [0, 100],
    warn: [0, 150],
    range: [0, 150],
    unit: ' ppb',
    rangeLabel: '0–150 ppb',
    goodLabel: 'Non-detectable'
  },
  // 19. Level - Range: 0-5 m (no US-EPA limit established)
  level_m_avg: {
    good: [1, 4],
    warn: [0.5, 5],
    range: [0, 5],
    unit: ' m',
    rangeLabel: '0–5 m',
    goodLabel: '—'
  },

  // 20. Conductivity (Electrical Conductivity) – typical potable water guideline up to ~1500 µS/cm
  // Range chosen to match device spec window commonly 0–2000 µS/cm
  conductivity_us_cm_avg: {
    good: [30, 1000],
    warn: [500, 1500],
    range: [0, 2000],
    unit: ' µS/cm',
    rangeLabel: '0–2000 µS/cm',
    goodLabel: '≤ 1500 µS/cm'
  }
};

export default parameterRanges;
